import { lazy } from "react";
import { isStaleChunkError } from "./staleChunk";

/**
 * Shared lazy-import factories for every route. Both `App.tsx` (for routing)
 * and `AdminSidebar.tsx` (for hover prefetch) import the same factory so
 * dynamic `import()` dedupes naturally — hovering a link warms the chunk
 * and the eventual click hits the in-memory cache.
 */

/**
 * Wrap a dynamic `import()` factory so a stale-chunk failure (common after a
 * fresh deploy) retries once after a short delay instead of immediately
 * surfacing as an error to React.lazy / Suspense.
 */
function withRetry<T>(factory: () => Promise<T>, attempts = 1): () => Promise<T> {
  return async () => {
    try {
      return await factory();
    } catch (err) {
      if (attempts > 0 && isStaleChunkError(err)) {
        await new Promise((r) => setTimeout(r, 250));
        return withRetry(factory, attempts - 1)();
      }
      throw err;
    }
  };
}

const rawFactories = {
  "/team": () => import("@/pages/Team"),
  "/profile": () => import("@/pages/Profile"),
  
  "/titles": () => import("@/pages/Titles"),
  
  "/reviews": () => import("@/pages/Reviews"),
  "/videos": () => import("@/pages/Ideas"),
  "/inbox": () => import("@/pages/Inbox"),
  "/messages": () => import("@/pages/Messages"),
  "/get-paid": () => import("@/pages/GetPaid"),
  "/emails": () => import("@/pages/Emails"),
  "/sops": () => import("@/pages/Sops"),
  "/debug": () => import("@/pages/Debug"),
} as const;

export const chunkFactories = Object.fromEntries(
  Object.entries(rawFactories).map(([k, f]) => [k, withRetry(f)]),
) as { [K in keyof typeof rawFactories]: (typeof rawFactories)[K] };

export type RoutePath = keyof typeof chunkFactories;

export const Team = lazy(chunkFactories["/team"]);
export const EditorProfile = lazy(withRetry(() => import("@/pages/EditorProfile")));
export const Profile = lazy(chunkFactories["/profile"]);

export const Titles = lazy(chunkFactories["/titles"]);

export const Reviews = lazy(chunkFactories["/reviews"]);
export const Ideas = lazy(chunkFactories["/videos"]);
export const Inbox = lazy(chunkFactories["/inbox"]);
export const Messages = lazy(chunkFactories["/messages"]);
export const GetPaid = lazy(chunkFactories["/get-paid"]);
export const Emails = lazy(chunkFactories["/emails"]);
export const Sops = lazy(chunkFactories["/sops"]);
export const Debug = lazy(chunkFactories["/debug"]);
export const NotFound = lazy(withRetry(() => import("@/pages/NotFound")));
export const Invite = lazy(withRetry(() => import("@/pages/Invite")));
export const Unsubscribe = lazy(withRetry(() => import("@/pages/Unsubscribe")));

export function prefetchRouteChunk(path: string): void {
  const factory = (chunkFactories as Record<string, () => Promise<unknown>>)[path];
  if (factory) void factory();
}
